import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  CreditCard, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  User
} from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSync } from "@/contexts/SyncContext";
import { listarContasPagar, criarContaPagar, atualizarContaPagar, deletarContaPagar, ContaPagar, calcularProximaExecucao, RecorrenciaContaPagar } from "@/integrations/supabase/contas-pagar";
import { listarContasCategorias, criarContaCategoria, deletarContaCategoria, ContaCategoria } from "@/integrations/supabase/contas-categorias";
import { listarFornecedores } from "@/integrations/supabase/fornecedores";
import { formatCurrency } from "@/utils/formatCurrency";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function ContasPagar() {
  const queryClient = useQueryClient();
  const { invalidateRelated } = useSync();
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");
  const [filtroCategoria, setFiltroCategoria] = useState<string>("todos");
  const [filtroMes, setFiltroMes] = useState<string>("todos");
  const [filtroAno, setFiltroAno] = useState<string>(new Date().getFullYear().toString());
  const [basePeriodo, setBasePeriodo] = useState<"vencimento" | "pagamento">("vencimento");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingConta, setEditingConta] = useState<ContaPagar | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFornecedorName, setSelectedFornecedorName] = useState<string>("");
  const [selectedContaIds, setSelectedContaIds] = useState<string[]>([]);
  const [recorrenciaLote, setRecorrenciaLote] = useState<RecorrenciaContaPagar>("mensal");
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isCategoriaDialogOpen, setIsCategoriaDialogOpen] = useState(false);
  const [novaCategoria, setNovaCategoria] = useState("");
  const [categoriaDeleting, setCategoriaDeleting] = useState<string | null>(null);
  const [isCategoriaSaving, setIsCategoriaSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<ContaPagar>>({
    descricao: "",
    categoria: "",
    valor_total: 0,
    valor_pago: 0,
    data_vencimento: "",
    data_pagamento: "",
    status: "pendente",
    forma_pagamento: "",
    observacoes: "",
    fornecedor_id: undefined,
    recorrencia: "unica",
    is_ativa: true,
    proxima_execucao: "",
    recorrencia_grupo_id: ""
  });

  // Buscar TODAS as contas para cálculo de estatísticas
  const { data: todasAsContas = [] } = useQuery({
    queryKey: ["contas_pagar_todas"],
    queryFn: () => listarContasPagar() // Sem filtro - busca TODAS
  });

  // Buscar contas filtradas para a lista
  const { data: contas = [], isLoading } = useQuery({
    queryKey: ["contas_pagar", filtroStatus],
    queryFn: () => listarContasPagar({ status: filtroStatus !== "todos" ? filtroStatus : undefined })
  });

  const { data: fornecedores = [] } = useQuery({
    queryKey: ["fornecedores"],
    queryFn: listarFornecedores
  });

  const { data: categorias = [] } = useQuery({
    queryKey: ["contas_categorias", "pagar"],
    queryFn: () => listarContasCategorias("pagar")
  });

  const meses = useMemo(() => (
    [
      { value: "1", label: "Janeiro" },
      { value: "2", label: "Fevereiro" },
      { value: "3", label: "Março" },
      { value: "4", label: "Abril" },
      { value: "5", label: "Maio" },
      { value: "6", label: "Junho" },
      { value: "7", label: "Julho" },
      { value: "8", label: "Agosto" },
      { value: "9", label: "Setembro" },
      { value: "10", label: "Outubro" },
      { value: "11", label: "Novembro" },
      { value: "12", label: "Dezembro" }
    ]
  ), []);

  const anosDisponiveis = useMemo(() => {
    const anos = new Set<number>();
    const anoAtual = new Date().getFullYear();
    anos.add(anoAtual);
    contas.forEach(conta => {
      const dataBase = conta.data_vencimento || conta.data_pagamento;
      if (dataBase) {
        anos.add(new Date(dataBase).getFullYear());
      }
    });
    return Array.from(anos).sort((a, b) => b - a).map(String);
  }, [contas]);

  const categoriasOrdenadas = useMemo(
    () => [...categorias].sort((a, b) => a.nome.localeCompare(b.nome)),
    [categorias]
  );

  const contasFiltradas = useMemo(() => {
    let filtradas = contas;
    const busca = searchTerm.trim().toLowerCase();

    if (filtroStatus !== "todos") {
      filtradas = filtradas.filter(conta => conta.status === filtroStatus);
    }

    if (filtroCategoria !== "todos") {
      filtradas = filtradas.filter(conta => (conta.categoria || "").toLowerCase() === filtroCategoria.toLowerCase());
    }

    if (filtroMes !== "todos") {
      filtradas = filtradas.filter(conta => {
        const dataBase = basePeriodo === "pagamento" ? conta.data_pagamento : conta.data_vencimento;
        if (!dataBase) {
          return false;
        }

        const data = new Date(dataBase);
        const mes = (data.getMonth() + 1).toString();
        const ano = data.getFullYear().toString();
        return mes === filtroMes && ano === filtroAno;
      });
    }

    if (busca) {
      filtradas = filtradas.filter(conta => {
        const fornecedor = fornecedores.find(f => f.id === conta.fornecedor_id);
        return (
          conta.descricao?.toLowerCase().includes(busca) ||
          conta.observacoes?.toLowerCase().includes(busca) ||
          conta.categoria?.toLowerCase().includes(busca) ||
          fornecedor?.nome_fantasia?.toLowerCase().includes(busca)
        );
      });
    }

    return filtradas;
  }, [contas, searchTerm, filtroStatus, filtroCategoria, filtroMes, filtroAno, basePeriodo, fornecedores]);

  // Calcular estatísticas usando TODAS as contas (independente do filtro)
  const estatisticas = useMemo(() => ({
    total: todasAsContas.reduce((acc, conta) => acc + conta.valor_total, 0),
    pago: todasAsContas.filter(c => c.status === 'pago').reduce((acc, conta) => acc + conta.valor_total, 0),
    pendente: todasAsContas.filter(c => c.status === 'pendente').reduce((acc, conta) => acc + conta.valor_total, 0),
    atrasado: todasAsContas.filter(c => c.status === 'atrasado').reduce((acc, conta) => acc + conta.valor_total, 0)
  }), [todasAsContas]);

  const resumoRecorrencias = useMemo(() => {
    const recorrentesAtivas = contas.filter(
      conta => conta.recorrencia && conta.recorrencia !== "unica" && conta.is_ativa
    );

    const proximaExecucao = recorrentesAtivas
      .map(conta => conta.proxima_execucao || conta.data_vencimento)
      .filter(Boolean)
      .sort()[0];

    const totalMensal = recorrentesAtivas.reduce((acc, conta) => {
      const valor = conta.valor_total || 0;
      switch (conta.recorrencia) {
        case "diaria":
          return acc + valor * 30;
        case "semanal":
          return acc + valor * 4;
        case "mensal":
          return acc + valor;
        case "anual":
          return acc + valor / 12;
        default:
          return acc;
      }
    }, 0);

    return {
      quantidade: recorrentesAtivas.length,
      proximaExecucao,
      totalMensal
    };
  }, [contas]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pago':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />Pago</Badge>;
      case 'pendente':
        return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
      case 'atrasado':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Atrasado</Badge>;
      case 'cancelado':
        return <Badge variant="outline">Cancelado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const gerarRecorrenciaGrupoId = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
    return `grupo-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  useEffect(() => {
    const idsDisponiveis = new Set(contasFiltradas.map(conta => conta.id).filter(Boolean) as string[]);
    setSelectedContaIds(prev => prev.filter(id => idsDisponiveis.has(id)));
  }, [contasFiltradas]);

  const handleToggleContaSelecionada = (id: string) => {
    setSelectedContaIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelecionarTodas = (checked: boolean) => {
    if (checked) {
      setSelectedContaIds(contasFiltradas.map(conta => conta.id).filter(Boolean) as string[]);
      return;
    }
    setSelectedContaIds([]);
  };

  const handleSalvarCategoria = async (nomeParam?: string) => {
    const nome = (nomeParam ?? novaCategoria).trim();
    if (!nome) {
      toast.error("Informe o nome da categoria");
      return;
    }

    if (categoriasOrdenadas.some(categoria => categoria.nome.toLowerCase() === nome.toLowerCase())) {
      toast.info("Categoria já cadastrada");
      return;
    }

    try {
      setIsCategoriaSaving(true);
      const result = await criarContaCategoria({ nome, tipo: "pagar" });
      if (result.ok) {
        toast.success("Categoria salva!");
        setNovaCategoria("");
        queryClient.invalidateQueries({ queryKey: ["contas_categorias", "pagar"] });
      } else {
        toast.error(result.error || "Erro ao salvar categoria");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro ao salvar categoria";
      toast.error(message);
    } finally {
      setIsCategoriaSaving(false);
    }
  };

  const handleExcluirCategoria = async (id: string) => {
    try {
      setCategoriaDeleting(id);
      const result = await deletarContaCategoria(id);
      if (result.ok) {
        toast.success("Categoria removida");
        queryClient.invalidateQueries({ queryKey: ["contas_categorias", "pagar"] });
        if (filtroCategoria !== "todos") {
          const categoriaAtual = categoriasOrdenadas.find(categoria => categoria.id === id);
          if (categoriaAtual && categoriaAtual.nome.toLowerCase() === filtroCategoria.toLowerCase()) {
            setFiltroCategoria("todos");
          }
        }
      } else {
        toast.error(result.error || "Erro ao remover categoria");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro ao remover categoria";
      toast.error(message);
    } finally {
      setCategoriaDeleting(null);
    }
  };

  const handleAplicarRecorrenciaEmLote = async () => {
    if (selectedContaIds.length === 0) {
      toast.info("Selecione ao menos uma conta");
      return;
    }

    if (!confirm("Aplicar recorrência às contas selecionadas?")) {
      return;
    }

    try {
      setIsBulkUpdating(true);
      const contasSelecionadas = contas.filter(conta => conta.id && selectedContaIds.includes(conta.id));
      const resultados = await Promise.all(
        contasSelecionadas.map(conta => {
          const recorrencia = recorrenciaLote;
          const proximaExecucao = calcularProximaExecucao(conta.data_vencimento, recorrencia);
          return atualizarContaPagar(conta.id!, {
            recorrencia,
            is_ativa: recorrencia !== "unica",
            proxima_execucao: recorrencia === "unica" ? null : proximaExecucao,
            recorrencia_grupo_id: recorrencia === "unica" ? conta.recorrencia_grupo_id : (conta.recorrencia_grupo_id || gerarRecorrenciaGrupoId())
          });
        })
      );

      const falhas = resultados.filter(result => !result.ok);
      if (falhas.length > 0) {
        toast.error("Algumas contas não foram atualizadas");
      } else {
        toast.success("Recorrência aplicada com sucesso");
      }

      invalidateRelated("contas_pagar");
      queryClient.invalidateQueries({ queryKey: ["contas_pagar", "fluxo"] });
      setSelectedContaIds([]);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro ao aplicar recorrência";
      toast.error(message);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleOpenDialog = (conta?: ContaPagar) => {
    if (conta) {
      setEditingConta(conta);
      const fornecedor = fornecedores.find(f => f.id === conta.fornecedor_id);
      setSelectedFornecedorName(fornecedor?.nome_fantasia || "");
      setFormData({
        ...conta,
        data_vencimento: conta.data_vencimento.split('T')[0],
        data_pagamento: conta.data_pagamento?.split('T')[0] || "",
        recorrencia: conta.recorrencia || "unica",
        is_ativa: conta.is_ativa !== undefined ? conta.is_ativa : true,
        proxima_execucao: conta.proxima_execucao || "",
        recorrencia_grupo_id: conta.recorrencia_grupo_id || ""
      });
    } else {
      setEditingConta(null);
      setSelectedFornecedorName("");
      setFormData({
        descricao: "",
        categoria: "",
        valor_total: 0,
        valor_pago: 0,
        data_vencimento: "",
        data_pagamento: "",
        status: "pendente",
        forma_pagamento: "",
        observacoes: "",
        fornecedor_id: undefined,
        recorrencia: "unica",
        is_ativa: true,
        proxima_execucao: "",
        recorrencia_grupo_id: ""
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingConta(null);
    setSelectedFornecedorName("");
  };

  const handleFornecedorChange = async (fornecedorName: string) => {
    setSelectedFornecedorName(fornecedorName);
    
    if (!fornecedorName) {
      setFormData({ ...formData, fornecedor_id: undefined });
      return;
    }
    
    // Buscar o ID do fornecedor pelo nome
    let fornecedor = fornecedores.find(f => f.nome_fantasia === fornecedorName);
    
    // Se o fornecedor não foi encontrado, recarregar lista de fornecedores
    // Isso pode acontecer quando um novo fornecedor foi criado
    if (!fornecedor && fornecedorName) {
      // Aguardar um pouco para garantir que o fornecedor foi salvo
      await new Promise(resolve => setTimeout(resolve, 300));
      await queryClient.refetchQueries({ queryKey: ["fornecedores"] });
      const updatedFornecedores = await listarFornecedores();
      fornecedor = updatedFornecedores.find(f => f.nome_fantasia === fornecedorName);
      
      if (fornecedor) {
        setFormData({ ...formData, fornecedor_id: fornecedor.id });
      } else {
        // Se ainda não encontrou, tentar mais uma vez após mais um delay
        await new Promise(resolve => setTimeout(resolve, 500));
        const finalFornecedores = await listarFornecedores();
        const finalFornecedor = finalFornecedores.find(f => f.nome_fantasia === fornecedorName);
        setFormData({ ...formData, fornecedor_id: finalFornecedor?.id || undefined });
      }
    } else if (fornecedor) {
      setFormData({ ...formData, fornecedor_id: fornecedor.id });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevenir múltiplos cliques

    if (!formData.descricao?.trim()) {
      toast.error("Descrição é obrigatória");
      return;
    }

    if (!formData.valor_total || formData.valor_total <= 0) {
      toast.error("Valor total deve ser maior que zero");
      return;
    }

    if (!formData.data_vencimento) {
      toast.error("Data de vencimento é obrigatória");
      return;
    }

    const recorrenciaSelecionada = (formData.recorrencia || "unica") as RecorrenciaContaPagar;
    const isRecorrente = recorrenciaSelecionada !== "unica";
    const proximaExecucao = isRecorrente
      ? calcularProximaExecucao(formData.data_vencimento, recorrenciaSelecionada)
      : null;
    const recorrenciaGrupoId = isRecorrente
      ? (formData.recorrencia_grupo_id || gerarRecorrenciaGrupoId())
      : null;

    setIsSubmitting(true);
    
    // Timeout de segurança para evitar travamento infinito
    const timeoutId = setTimeout(() => {
      setIsSubmitting(false);
      toast.error("Operação está demorando muito. Tente novamente.");
    }, 30000); // 30 segundos

    try {
      // Se um nome de fornecedor foi selecionado mas não encontramos o ID, buscar novamente
      if (selectedFornecedorName && !formData.fornecedor_id) {
        await queryClient.refetchQueries({ queryKey: ["fornecedores"] });
        const updatedFornecedores = await listarFornecedores();
        const fornecedorAtualizado = updatedFornecedores.find(f => f.nome_fantasia === selectedFornecedorName);
        if (fornecedorAtualizado) {
          formData.fornecedor_id = fornecedorAtualizado.id;
        }
      }

      const payloadBase = {
        ...formData,
        recorrencia: recorrenciaSelecionada,
        is_ativa: isRecorrente ? (formData.is_ativa ?? true) : false,
        proxima_execucao: proximaExecucao,
        recorrencia_grupo_id: recorrenciaGrupoId
      };

      if (editingConta?.id) {
        const result = await atualizarContaPagar(editingConta.id, payloadBase);
        clearTimeout(timeoutId);
        if (result.ok) {
          toast.success("Conta atualizada com sucesso!");
          invalidateRelated('contas_pagar');
          // Invalidar também queries do Fluxo de Caixa
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0];
              return key === 'contas_pagar' && query.queryKey[1] === 'fluxo';
            }
          });
          handleCloseDialog();
        } else {
          toast.error(result.error || "Erro ao atualizar conta");
        }
      } else {
        const result = await criarContaPagar(payloadBase as Omit<ContaPagar, 'id' | 'created_at' | 'updated_at' | 'empresa_id' | 'valor_pago'>);
        clearTimeout(timeoutId);
        if (result.ok) {
          toast.success("Conta criada com sucesso!");
          invalidateRelated('contas_pagar');
          // Invalidar também queries do Fluxo de Caixa
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0];
              return key === 'contas_pagar' && query.queryKey[1] === 'fluxo';
            }
          });
          handleCloseDialog();
        } else {
          toast.error(result.error || "Erro ao criar conta");
        }
      }
    } catch (error: unknown) {
      clearTimeout(timeoutId);
      const message = error instanceof Error ? error.message : "Erro ao salvar conta";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir esta conta?")) {
      return;
    }

    try {
      setIsDeleting(id);
      const result = await deletarContaPagar(id);
      if (result.ok) {
        toast.success("Conta excluída com sucesso!");
        invalidateRelated('contas_pagar');
        // Invalidar também queries do Fluxo de Caixa
        queryClient.invalidateQueries({ 
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'contas_pagar' && query.queryKey[1] === 'fluxo';
          }
        });
      } else {
        toast.error(result.error || "Erro ao excluir conta");
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro ao excluir conta";
      toast.error(message);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleMarcarComoPago = async (conta: ContaPagar) => {
    const result = await atualizarContaPagar(conta.id!, {
      valor_pago: conta.valor_total,
      status: 'pago',
      data_pagamento: new Date().toISOString().split('T')[0]
    });

    if (result.ok) {
      toast.success("Conta marcada como paga!");
      invalidateRelated('contas_pagar');
      // Invalidar também queries do Fluxo de Caixa
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'contas_pagar' && query.queryKey[1] === 'fluxo';
        }
      });
    } else {
      toast.error(result.error || "Erro ao atualizar conta");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <PageHeader
        title="Contas a Pagar"
        icon={<CreditCard className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
      />

      <div className="container mx-auto p-6 space-y-6">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-white to-purple-50 hover:shadow-lg transition-all">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-purple-600">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency({ value: estatisticas.total, currency: 'BRL' })}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200 bg-gradient-to-br from-white to-green-50 hover:shadow-lg transition-all">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-green-600">Pago</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-700">{formatCurrency({ value: estatisticas.pago, currency: 'BRL' })}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-yellow-200 bg-gradient-to-br from-white to-yellow-50 hover:shadow-lg transition-all">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-yellow-600">Pendente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-700">{formatCurrency({ value: estatisticas.pendente, currency: 'BRL' })}</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-red-200 bg-gradient-to-br from-white to-red-50 hover:shadow-lg transition-all">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-red-600">Atrasado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-700">{formatCurrency({ value: estatisticas.atrasado, currency: 'BRL' })}</div>
            </CardContent>
          </Card>
        </div>

        {/* Resumo de Recorrências */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Recorrências ativas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{resumoRecorrencias.quantidade}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Próxima execução</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {resumoRecorrencias.proximaExecucao
                  ? format(new Date(resumoRecorrencias.proximaExecucao), "dd/MM/yyyy", { locale: ptBR })
                  : "-"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total mensal recorrente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency({ value: resumoRecorrencias.totalMensal, currency: "BRL" })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar contas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="pago">Pago</SelectItem>
                <SelectItem value="atrasado">Atrasado</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas categorias</SelectItem>
                {categoriasOrdenadas.map(categoria => (
                  <SelectItem key={categoria.id} value={categoria.nome}>
                    {categoria.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={basePeriodo} onValueChange={(value) => setBasePeriodo(value as "vencimento" | "pagamento")}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Base do período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vencimento">Base: Vencimento</SelectItem>
                <SelectItem value="pagamento">Base: Pagamento</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filtroMes} onValueChange={setFiltroMes}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Mês" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os meses</SelectItem>
                {meses.map(mes => (
                  <SelectItem key={mes.value} value={mes.value}>
                    {mes.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroAno} onValueChange={setFiltroAno}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Ano" />
              </SelectTrigger>
              <SelectContent>
                {anosDisponiveis.map(ano => (
                  <SelectItem key={ano} value={ano}>
                    {ano}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setIsCategoriaDialogOpen(true)}>
              Categorias
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-purple-500 hover:bg-purple-600 text-white font-semibold">
              <Plus className="h-4 w-4 mr-2" />
              Nova Conta
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-4 rounded-lg border bg-white p-3">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedContaIds.length > 0 && selectedContaIds.length === contasFiltradas.length}
                onCheckedChange={(checked) => handleSelecionarTodas(!!checked)}
              />
              <span className="text-sm">Selecionar todas ({selectedContaIds.length}/{contasFiltradas.length})</span>
            </div>
            <Select value={recorrenciaLote} onValueChange={(value) => setRecorrenciaLote(value as RecorrenciaContaPagar)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Recorrência" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unica">Única</SelectItem>
                <SelectItem value="diaria">Diária</SelectItem>
                <SelectItem value="semanal">Semanal</SelectItem>
                <SelectItem value="mensal">Mensal</SelectItem>
                <SelectItem value="anual">Anual</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={handleAplicarRecorrenciaEmLote}
              disabled={isBulkUpdating || selectedContaIds.length === 0}
            >
              {isBulkUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Aplicando...
                </>
              ) : (
                "Aplicar recorrência"
              )}
            </Button>
          </div>
        </div>

        {/* Lista de Contas */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">Carregando contas...</p>
          </div>
        ) : contasFiltradas.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhuma conta encontrada</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contasFiltradas.map((conta) => {
              const fornecedor = fornecedores.find(f => f.id === conta.fornecedor_id);
              return (
                <Card key={conta.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {conta.id && (
                            <Checkbox
                              checked={selectedContaIds.includes(conta.id)}
                              onCheckedChange={() => handleToggleContaSelecionada(conta.id!)}
                            />
                          )}
                          <h3 className="font-semibold">{conta.descricao}</h3>
                          {getStatusBadge(conta.status)}
                        </div>
                        {fornecedor && (
                          <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Fornecedor: {fornecedor.nome_fantasia}
                          </p>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Valor Total</p>
                            <p className="font-semibold">{formatCurrency({ value: conta.valor_total, currency: 'BRL' })}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Valor Pago</p>
                            <p className="font-semibold">{formatCurrency({ value: conta.valor_pago, currency: 'BRL' })}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Vencimento</p>
                            <p className="font-semibold">
                              {format(new Date(conta.data_vencimento), "dd/MM/yyyy", { locale: ptBR })}
                            </p>
                          </div>
                          {conta.data_pagamento && (
                            <div>
                              <p className="text-muted-foreground">Pagamento</p>
                              <p className="font-semibold">
                                {format(new Date(conta.data_pagamento), "dd/MM/yyyy", { locale: ptBR })}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {conta.status !== 'pago' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarcarComoPago(conta)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Marcar como Pago
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDialog(conta)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(conta.id!)}
                          disabled={isDeleting === conta.id}
                        >
                          {isDeleting === conta.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Dialog de categorias */}
        <Dialog open={isCategoriaDialogOpen} onOpenChange={setIsCategoriaDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Categorias de contas a pagar</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nova-categoria">Nova categoria</Label>
                <div className="flex gap-2">
                  <Input
                    id="nova-categoria"
                    value={novaCategoria}
                    onChange={(e) => setNovaCategoria(e.target.value)}
                    placeholder="Ex: Energia, Aluguel"
                  />
                  <Button type="button" onClick={() => handleSalvarCategoria()} disabled={isCategoriaSaving}>
                    {isCategoriaSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Salvar"
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Categorias salvas</Label>
                <div className="space-y-2 max-h-60 overflow-auto rounded-md border p-2">
                  {categoriasOrdenadas.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhuma categoria cadastrada.</p>
                  ) : (
                    categoriasOrdenadas.map((categoria) => (
                      <div key={categoria.id} className="flex items-center justify-between gap-2">
                        <span className="text-sm">{categoria.nome}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleExcluirCategoria(categoria.id!)}
                          disabled={categoriaDeleting === categoria.id}
                        >
                          {categoriaDeleting === categoria.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCategoriaDialogOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de criar/editar */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingConta ? "Editar Conta a Pagar" : "Nova Conta a Pagar"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição *</Label>
                <Input
                  id="descricao"
                  value={formData.descricao || ""}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fornecedor">Fornecedor</Label>
                  <Select
                    value={selectedFornecedorName}
                    onValueChange={handleFornecedorChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um fornecedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {fornecedores.map((fornecedor) => (
                        <SelectItem key={fornecedor.id} value={fornecedor.nome_fantasia}>
                          {fornecedor.nome_fantasia}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoria</Label>
                  <div className="flex flex-col gap-2">
                    <Input
                      id="categoria"
                      list="categorias-contas"
                      value={formData.categoria || ""}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    />
                    <datalist id="categorias-contas">
                      {categoriasOrdenadas.map((categoria) => (
                        <option key={categoria.id} value={categoria.nome} />
                      ))}
                    </datalist>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const nome = (formData.categoria || "").trim();
                          if (!nome) {
                            toast.info("Digite uma categoria para salvar");
                            return;
                          }
                          handleSalvarCategoria(nome);
                        }}
                        disabled={isCategoriaSaving}
                      >
                        {isCategoriaSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          "Salvar categoria"
                        )}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setIsCategoriaDialogOpen(true)}>
                        Gerenciar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valor_total">Valor Total *</Label>
                  <Input
                    id="valor_total"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valor_total || 0}
                    onChange={(e) => setFormData({ ...formData, valor_total: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="valor_pago">Valor Pago</Label>
                  <Input
                    id="valor_pago"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valor_pago || 0}
                    onChange={(e) => setFormData({ ...formData, valor_pago: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data_vencimento">Data de Vencimento *</Label>
                  <Input
                    id="data_vencimento"
                    type="date"
                    value={formData.data_vencimento || ""}
                    onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_pagamento">Data de Pagamento</Label>
                  <Input
                    id="data_pagamento"
                    type="date"
                    value={formData.data_pagamento || ""}
                    onChange={(e) => setFormData({ ...formData, data_pagamento: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
                  <Select
                    value={formData.forma_pagamento || ""}
                    onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="cartao_debito">Cartão Débito</SelectItem>
                      <SelectItem value="cartao_credito">Cartão Crédito</SelectItem>
                      <SelectItem value="boleto">Boleto</SelectItem>
                      <SelectItem value="transferencia">Transferência</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status || "pendente"}
                    onValueChange={(value) => setFormData({ ...formData, status: value as ContaPagar["status"] })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="pago">Pago</SelectItem>
                      <SelectItem value="atrasado">Atrasado</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recorrencia">Recorrência</Label>
                  <Select
                    value={formData.recorrencia || "unica"}
                    onValueChange={(value: RecorrenciaContaPagar) => setFormData({ ...formData, recorrencia: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unica">Única</SelectItem>
                      <SelectItem value="diaria">Diária</SelectItem>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ativar recorrência</Label>
                  <div className="flex items-center gap-2 rounded-md border p-2">
                    <Checkbox
                      checked={!!formData.is_ativa}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_ativa: !!checked })}
                      disabled={(formData.recorrencia || "unica") === "unica"}
                    />
                    <span className="text-sm text-muted-foreground">
                      Gerar próximas contas automaticamente
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes || ""}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingConta ? "Atualizando..." : "Criando..."}
                    </>
                  ) : (
                    editingConta ? "Atualizar" : "Criar"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

